import {
  FILE_ID_RE,
  KEY_B64URL_LENGTH,
  KEY_SIZE,
  PASSWORD_SALT_B64URL_LENGTH,
  PASSWORD_SALT_SIZE,
  TOKEN_B64URL_LENGTH,
  TRANSFER_ID_RE,
  WRAPPED_KEY_B64URL_LENGTH,
  WRAPPED_KEY_SIZE,
} from "./constants.js";
import { fromBase64Url, toBase64Url } from "./bytes.js";
import { LscError } from "./errors.js";
import { isValidToken } from "./tokens.js";

export type PasswordKdfId = "argon2id" | "pbkdf2";

/**
 * §7 — URL fragment contents. Never leaves the browser.
 * plain:    "1.{key}.{token}"                — key is MK (transfer link) or K_file (per-file link)
 * password: "1p.{wrappedKey}.{salt}.{token}" — argon2id
 *           "1q.{wrappedKey}.{salt}.{token}" — pbkdf2 fallback
 */
export type Fragment =
  | { kind: "plain"; key: Uint8Array; token: string }
  | { kind: "password"; kdf: PasswordKdfId; wrappedKey: Uint8Array; salt: Uint8Array; token: string };

const B64_FIELD = /^[A-Za-z0-9_-]+$/;

export function buildFragment(f: Fragment): string {
  if (!isValidToken(f.token)) throw new LscError("BAD_INPUT", "invalid token");
  if (f.kind === "plain") {
    if (f.key.length !== KEY_SIZE) throw new LscError("BAD_INPUT", "key must be 32 bytes");
    return `1.${toBase64Url(f.key)}.${f.token}`;
  }
  if (f.wrappedKey.length !== WRAPPED_KEY_SIZE) throw new LscError("BAD_INPUT", "wrappedKey must be 60 bytes");
  if (f.salt.length !== PASSWORD_SALT_SIZE) throw new LscError("BAD_INPUT", "salt must be 16 bytes");
  const ver = f.kdf === "argon2id" ? "1p" : "1q";
  return `${ver}.${toBase64Url(f.wrappedKey)}.${toBase64Url(f.salt)}.${f.token}`;
}

/** Accepts the fragment with or without the leading "#". */
export function parseFragment(fragment: string | null | undefined): Fragment {
  if (typeof fragment !== "string") throw new LscError("FRAGMENT_MISSING");
  const raw = fragment.startsWith("#") ? fragment.slice(1) : fragment;
  if (raw.length === 0) throw new LscError("FRAGMENT_MISSING");
  const parts = raw.split(".");
  for (const p of parts) {
    if (!B64_FIELD.test(p)) throw new LscError("BAD_FRAGMENT", "invalid field");
  }
  const ver = parts[0];
  if (ver === "1") {
    if (parts.length !== 3) throw new LscError("BAD_FRAGMENT", "expected 3 fields");
    const [, k, t] = parts as [string, string, string];
    if (k.length !== KEY_B64URL_LENGTH || t.length !== TOKEN_B64URL_LENGTH) {
      throw new LscError("BAD_FRAGMENT", "invalid field length");
    }
    return { kind: "plain", key: fromBase64Url(k), token: t };
  }
  if (ver === "1p" || ver === "1q") {
    if (parts.length !== 4) throw new LscError("BAD_FRAGMENT", "expected 4 fields");
    const [, w, s, t] = parts as [string, string, string, string];
    if (
      w.length !== WRAPPED_KEY_B64URL_LENGTH ||
      s.length !== PASSWORD_SALT_B64URL_LENGTH ||
      t.length !== TOKEN_B64URL_LENGTH
    ) {
      throw new LscError("BAD_FRAGMENT", "invalid field length");
    }
    return {
      kind: "password",
      kdf: ver === "1p" ? "argon2id" : "pbkdf2",
      wrappedKey: fromBase64Url(w),
      salt: fromBase64Url(s),
      token: t,
    };
  }
  throw new LscError("BAD_FRAGMENT", `unknown fragment version "${ver}"`);
}

export function buildTransferUrl(origin: string, transferId: string, fragment: Fragment | string): string {
  if (!TRANSFER_ID_RE.test(transferId)) throw new LscError("BAD_INPUT", "invalid transferId");
  const f = typeof fragment === "string" ? fragment : buildFragment(fragment);
  return `${trimSlash(origin)}/d/${transferId}#${f}`;
}

export function buildFileUrl(origin: string, transferId: string, fileId: string, fragment: Fragment | string): string {
  if (!TRANSFER_ID_RE.test(transferId)) throw new LscError("BAD_INPUT", "invalid transferId");
  if (!FILE_ID_RE.test(fileId)) throw new LscError("BAD_INPUT", "invalid fileId");
  const f = typeof fragment === "string" ? fragment : buildFragment(fragment);
  return `${trimSlash(origin)}/f/${transferId}/${fileId}#${f}`;
}

/** Strips the fragment from any URL string before it is logged or sent anywhere. */
export function stripFragment(url: string): string {
  const i = url.indexOf("#");
  return i === -1 ? url : url.slice(0, i);
}

function trimSlash(origin: string): string {
  return origin.endsWith("/") ? origin.slice(0, -1) : origin;
}
